import "./wdyr";
import "./react-devtools-hook";
import ReactDOM from "react-dom";
import App from "./App";
import * as serviceWorker from "./serviceWorker";
import Amplify, { Auth } from "aws-amplify";
import config from "./configAmplify";
import * as Sentry from "@sentry/react";
import { isTrackedClientRoute } from "observability/performanceLogger";

const parseSampleRate = (value: string | undefined, fallback: number) => {
    if (!value) {
        return fallback;
    }

    const parsedValue = Number(value);
    if (Number.isNaN(parsedValue)) {
        return fallback;
    }

    return parsedValue;
};

const sentryApiOrigin = (() => {
    try {
        return new URL(import.meta.env.VITE_API_ADDRESS).origin;
    } catch {
        return undefined;
    }
})();

const defaultTraceSampleRate = parseSampleRate(
    import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE,
    0.02
);

Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    release: import.meta.env.VITE_SENTRY_RELEASE,
    integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
            maskAllText: false,
            blockAllMedia: false
        })
    ],
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT,
    maxBreadcrumbs: 30,
    replaysSessionSampleRate: parseSampleRate(
        import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE,
        0
    ),
    replaysOnErrorSampleRate: parseSampleRate(
        import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE,
        1
    ),
    tracesSampler: (samplingContext) => {
        const transactionName =
            samplingContext.name || window.location.pathname || "/";

        if (isTrackedClientRoute(transactionName)) {
            return 0.2;
        }

        return defaultTraceSampleRate;
    },
    tracePropagationTargets: [
        /^\//,
        "https://www.compleo.app",
        "https://compleo.app",
        ...(sentryApiOrigin ? [sentryApiOrigin] : [])
    ]
});

Amplify.configure({
    Auth: {
        mandatorySignIn: true,
        region: config.cognito.REGION,
        userPoolId: config.cognito.USER_POOL_ID,
        identityPoolId: config.cognito.IDENTITY_POOL_ID,
        userPoolWebClientId: config.cognito.APP_CLIENT_ID
    },
    API: {
        endpoints: [
            {
                name: "CandidatoGatewayAPI",
                endpoint: `${config.apiGateway.URL}/candidato`,
                custom_header: async () => {
                    // return { Authorization : 'token' }
                    return {
                        Authorization: (await Auth.currentSession())
                            .getIdToken()
                            .getJwtToken()
                    };
                    // Alternatively, with Cognito User Pools use this:
                    // return { Authorization: (await Auth.currentSession()).idToken.jwtToken }
                }
            },
            {
                name: "UserGatewayAPI",
                endpoint: `${config.apiGateway.URL}/user`,
                custom_header: async () => {
                    // return { Authorization : 'token' }
                    return {
                        Authorization: (await Auth.currentSession())
                            .getIdToken()
                            .getJwtToken()
                    };
                    // Alternatively, with Cognito User Pools use this:
                    // return { Authorization: (await Auth.currentSession()).idToken.jwtToken }
                }
            },
            {
                name: "GeneralGatewayAPI",
                endpoint: `${config.apiGateway.URL}/general`,
                custom_header: async () => {
                    // return { Authorization : 'token' }
                    return {
                        Authorization: (await Auth.currentSession())
                            .getIdToken()
                            .getJwtToken()
                    };
                    // Alternatively, with Cognito User Pools use this:
                    // return { Authorization: (await Auth.currentSession()).idToken.jwtToken }
                }
            },
            {
                name: "RouteApi",
                endpoint: `${config.apiGateway.URL}`,
                custom_header: async () => {
                    // return { Authorization : 'token' }
                    return {
                        Authorization: (await Auth.currentSession())
                            .getIdToken()
                            .getJwtToken()
                    };
                    // Alternatively, with Cognito User Pools use this:
                    // return { Authorization: (await Auth.currentSession()).idToken.jwtToken }
                }
            }
        ]
    }
});

const rootContainer = document.getElementById("root");

ReactDOM.render(<App />, rootContainer);
// debugContextDevtool(rootContainer, {
//     disable: process.env.NODE_ENV === "production"
// });

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
